import React, { useEffect, useRef, useState } from "react";
import IconButton from "../kit/IconButton";
import ActionTile from "../kit/ActionTile";
import Badge from "../kit/Badge";
import Chip from "../kit/Chip";
import { IoIosLink, IoMdClose } from "react-icons/io";
import { IoFilter } from "react-icons/io5";
import ProfilePicture from "../icons/ProfilePicture";
import Loader from "../icons/Loader";
import { TbPhotoShare, TbShare3, TbInfoSquareRoundedFilled } from "react-icons/tb";
import { useUser } from "../../context/UserContext/UserProvider";
import { useData } from "../../context/DataContext/DataProvider";
import { enqueueSnackbar } from "notistack";
import axios from "../../api/axiosInstance";
import { FiSearch } from "react-icons/fi";
import { BottomSheetDateFilter } from "../Map/Map";
import Input from "../Input/Input";
import GlobalSearchFilters from "../GlobalSearchFilters/GlobalSearchFilters";
import InfiniteScroller from "../InfiniteScroller/InfiniteScroller";
import EventCard from "../EventCard/EventCard";
import { useAccountDetails } from "../../context/AccountDetailsContext/AccountDetailsProvider";
import { useEventDetails } from "../../context/EventDetailsProvider/EventDetailsProvider";
import { backendUrl } from "../../api/constants";
import { Share } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";
import { Instagram } from "capacitor-instagram-share";
import { modal } from "../../util/useModal";
import Tabs, { TabContent } from "../ui/tabs";
import { QRCode } from "react-qrcode-logo";
import { useUIContext } from "../../context/UIContext/UIContext";
import { IoCopyOutline } from "react-icons/io5";
import { MdQrCode2 } from "react-icons/md";
import { FaInstagram } from "react-icons/fa";
import SocialsView, { hasMediaEmbeds } from "../SocialsInput/SocialsView";
import { FaEye, FaRegEye } from "react-icons/fa6";
import { domToPng } from 'modern-screenshot';
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Clipboard } from "@capacitor/clipboard";
import { FACEBOOK_APP_ID } from "../../api/constants";
import { useLogger } from "../../context/LogContext/LogProvider";
import AppStatusScreen from "../AppStatusScreen/AppStatusScreen";
import KitButton from "../kit/Button";

const AccountDetails = ({ parentRef, Close }) => {
  const [showFullBio, setShowFullBio] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const characterLimit = 200;
  const { user } = useUser();
  const { startLog } = useLogger();
  const searchInputRef = useRef();

  // Handle Enter key to dismiss keyboard on iOS
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Blur the input to dismiss the keyboard
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  };

  const { availableApps } = useUIContext();
  const { toggleFollowUser } = useData();
  const {
    selectedAccount,
    queryFilter,
    setQueryFilter,
    dateFilter,
    setDateFilter,
    tagFilter,
    setTagFilter,
    timezone,
    setTimezone,
    accountDetailsSearchLoading,
    filteredEvents,
    filtersActive,
    setSelectedAccount,
  } = useAccountDetails();
  const { selectEvent, selectedEvent } = useEventDetails();
  const account = selectedAccount;

  // Update timezone when AccountDetails component mounts or selectedEvent changes
  useEffect(() => {
    if (selectedEvent?.timezone?.[0]) {
      setTimezone(selectedEvent.timezone[0]);
    } else {
      setTimezone("UTC");
    }
    console.log("timezone ", timezone);
  }, [selectedEvent?.timezone, setTimezone]);

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    if (!hex) return `rgba(255, 255, 255, ${alpha})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Create elegant dark gradient for accounts
  const createAccountGradient = () => {
    // Sophisticated dark gradient with subtle depth
    return `
      radial-gradient(circle at 20% 20%, rgba(70, 70, 80, 0.6) 0%, transparent 65%),
      radial-gradient(circle at 80% 30%, rgba(60, 65, 75, 0.5) 0%, transparent 60%),
      radial-gradient(circle at 30% 80%, rgba(55, 60, 70, 0.4) 0%, transparent 55%),
      radial-gradient(circle at 70% 70%, rgba(50, 55, 65, 0.3) 0%, transparent 50%),
      linear-gradient(135deg, #3c3c44 0%, #2a2a32 100%)
    `;
  };

  const shareAccount = async () => {
    try {
    const isNative = Capacitor.isNativePlatform();
    const result = await modal(
      ({ proceed }) => {
        // const [load, setLoad] = useState(false);
        const [tab, setTab] = useState("image");
        const fullimageref = useRef(null);
        const imgref = useRef(null);
        const stickerRef = useRef(null);
        const qrcoderef = useRef(null);

        //#region handlers
        // Image capture using modern-screenshot
        const getAssetFile = async (element, url) => {
          let data;
          if (url) {
            data = url;
          } else {
            try {
              // Get actual dimensions to avoid shrinking
              const rect = element.getBoundingClientRect();
              const computedStyle = window.getComputedStyle(element);
              const actualWidth = Math.max(element.scrollWidth, element.offsetWidth, rect.width);
              const actualHeight = Math.max(element.scrollHeight, element.offsetHeight, rect.height);
              
              // Use modern-screenshot for image capture
              data = await domToPng(element, {
                width: actualWidth,
                height: actualHeight,
                scale: window.devicePixelRatio || 2,
                backgroundColor: null,
                style: {
                  margin: '0',
                  border: '0',
                  borderRadius: computedStyle.borderRadius,
                  minWidth: 'unset',
                  maxWidth: 'unset',
                  width: `${actualWidth}px`,
                  height: `${actualHeight}px`
                }
              });
              
            } catch (error) {
              throw error;
            }
          }
          
          const filename = `${Date.now()}.png`;
          await Filesystem.writeFile({
            path: filename,
            data: data,
            directory: Directory.Cache,
          });
          const uriResult = await Filesystem.getUri({
            directory: Directory.Cache,
            path: filename,
          });
          return { url: uriResult.uri, data: data };
        };

        const shareImage = async () => {
          const log = startLog({
            user: user,
            event: "share image",
          });
          try {
            setTab("image");
            log.log("tab set to image");
            const { url: uri, data: base64img } = await getAssetFile(
              fullimageref.current
            );
            log.log("using capacitor share");
            await Share.share({
              files: [uri],
              url: `https://app.inyc.us/#/?account=${selectedAccount?._id}`,
              title: `Check out this ${
                selectedAccount?.accountType || "user"
              } on in—yc`,
            });

            log.log("[Success] shared");
          } catch (error) {
            log.log("[Error] ", error);
            console.error(error);
          } finally {
            // updateShare();
            log.display();
          }
        };

        const copyLink = async () => {
          setTab("image");
          const shareContent = `https://app.inyc.us/#/?account=${selectedAccount?._id}`;

          try {
            if (isNative) {
              await Clipboard.write({ string: shareContent });
            } else {
              await navigator.clipboard.writeText(shareContent);
            }
            enqueueSnackbar("link copied to clipboard.", {
              variant: "success",
            });
            // updateShare();
          } catch (error) {
            console.error("Error copying account share link:", error);
            enqueueSnackbar("error copying content to clipboard.", {
              variant: "error",
            });
          }
        };

        const shareLink = async () => {
          const shareContent = `https://app.inyc.us/#/?account=${selectedAccount?._id}`;
          try {
            const title = `Check out this ${
              selectedAccount?.accountType || "user"
            } on in—yc`;
            if (isNative) {
              await Share.share({
                url: shareContent,
                title,
              });
            } else if (navigator.share) {
              await navigator.share({
                url: shareContent,
                title,
              });
            } else {
              await Share.share({
                url: shareContent,
                title,
              });
            }
            // updateShare();
          } catch (error) {
            if (error?.name !== "AbortError") {
              console.error("Error sharing account link:", error);
              enqueueSnackbar("unable to open share options.", {
                variant: "error",
              });
            }
          }
        };

        const shareInstagramStory = async () => {
          const log = startLog({
            user: user,
            event: "open instagram",
          });
          const result = await modal(
            ({ proceed }) => {
              return (
                <AppStatusScreen
                  title="share to instagram stories"
                  hideLogo
                  centerClassName="mt-kit-16"
                  body={
                    <>
                      <p>we've copied your account link for you</p>
                      <p>on instagram:</p>
                      <p>tap: “sticker” then “link”</p>
                      <p>paste the link, add custom text</p>
                    </>
                  }
                  actions={
                    <div className="flex w-full items-center justify-center gap-kit-8">
                      <KitButton fullWidth className="max-w-[9.4rem]" onClick={() => proceed(false)}>
                        cancel
                      </KitButton>
                      <KitButton variant="primary" fullWidth className="max-w-[9.4rem]" onClick={() => proceed(true)}>
                        share
                      </KitButton>
                    </div>
                  }
                />
              );
            },
            {
              raw: true,
              fullScreen: true,
            }
          );
          if (result) {
            try {
              try {
                const { url: stickerUri } = await getAssetFile(
                  stickerRef.current
                );
                const shareUrl = `https://app.inyc.us/#/?account=${selectedAccount?._id}`;
                // Pre-encode the hash portion to prevent Instagram from mangling it
                const encodedShareUrl = shareUrl.replace('#/', encodeURIComponent('#/'));

                await Clipboard.write({
                  string: encodedShareUrl,
                });
                await Instagram.shareToInstagramStory({
                  appID: FACEBOOK_APP_ID,
                  stickerImage: stickerUri,
                  shareUrl: encodedShareUrl,
                });
                log.log("opened instagram");
              } catch (err) {
                log.log("[Error] error opening instagram", JSON.stringify(err));
                log.log("[Error] ", err.message);
              }
            } catch (error) {
              log.log("[Error] ", error.message);
            } finally {
              log.display();
              // updateShare();
            }
          }
        };

        const shareQrCode = async () => {
          setTab("qr");
          const { url: qrUri } = await getAssetFile(qrcoderef.current);
          await Share.share({
            files: [qrUri],
            title: `Check out this ${
              selectedAccount?.accountType || "user"
            } on in—yc`,
          });
          // updateShare();
        };
        //#endregion

        return (
          <div className="min-h-[100vh] flex flex-col justify-between items-center">
            <div className="flex w-full justify-center items-center flex-1">
              <Tabs
                staticHeight
                contentWrapperClass="items-center"
                className="w-full"
                tabs={["image", "qr"]}
                hideTabs
                currentTab={tab}
                setCurrentTab={setTab}
              >
                <TabContent name="image">
                  <div
                    ref={fullimageref}
                    className="relative overflow-hidden h-[590px] flex items-center justify-center w-[350px] mx-auto mb-kit-16 rounded-kit-lg"
                  >
                    <div
                      ref={imgref}
                      className={`absolute top-0 left-0 w-full h-full `}
                      style={{
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundImage: createAccountGradient(),
                      }}
                    ></div>
                    <div
                      ref={stickerRef}
                      className={`relative z-[1] text-start duration-300 p-kit-24 rounded-kit-xl min-w-[200px] max-w-[70%] backdrop-blur-md border border-white/20`}
                      style={{
                        background: `
                          ${createAccountGradient()},
                          linear-gradient(135deg, 
                            rgba(255, 255, 255, 0.25) 0%, 
                            rgba(255, 255, 255, 0.1) 100%
                          )
                        `,
                        boxShadow: `
                          0 25px 50px -12px rgba(0, 0, 0, 0.25),
                          inset 0 1px 0 rgba(255, 255, 255, 0.1)
                        `,
                      }}
                    >
                      <div className="mb-kit-20">
                        <div 
                          className="mx-auto mb-kit-16 w-[60px] h-[60px] rounded-kit-full p-kit-4 backdrop-blur-sm border border-white/30"
                          style={{
                            background: `linear-gradient(135deg, 
                              rgba(255, 255, 255, 0.3) 0%, 
                              rgba(255, 255, 255, 0.1) 100%
                            )`,
                            boxShadow: `0 8px 24px rgba(0, 0, 0, 0.15)`,
                          }}
                        >
                          <ProfilePicture
                            profilePicture={selectedAccount?.profilePicture}
                            username={
                              selectedAccount?.firstName?.charAt(0) +
                              selectedAccount?.lastName?.charAt(0)
                            }
                            style={{
                              width: "100%",
                              height: "100%",
                              border: "none",
                            }}
                          />
                        </div>
                        <div
                          className="text-center mb-[-1rem]"
                        >
                          <div className="text-white text-center break-words text-kit-md font-['Primary-Bold'] mb-kit-6">
                            {selectedAccount?.businessName ||
                              selectedAccount?.firstName +
                                " " +
                                selectedAccount?.lastName}
                          </div>
                          {selectedAccount?.businessName && (
                            <div className="text-white/80 text-kit-base mb-kit-6 font-['Primary-Medium']">
                              {selectedAccount?.firstName +
                                " " +
                                selectedAccount?.lastName}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="gap-kit-6 flex items-center justify-center text-white">
                        <div className="text-kit-sm relative text-white">
                          check them out on
                        </div>
                        <img
                          className="h-[15px]"
                          src={`${backendUrl}/icon_white_nobg.png`}
                        />
                      </div>
                    </div>
                  </div>
                </TabContent>
                <TabContent
                  name="qr"
                  className="flex items-center justify-center"
                >
                  <div ref={qrcoderef}
                    className="rounded-[var(--inyc-input-border-radius)] overflow-hidden"
                  >
                    <QRCode
                      qrStyle="fluid"
                      eyeRadius={10}
                      value={`https://app.inyc.us/#/?account=${selectedAccount?._id}`}
                      size={200}
                    />
                  </div>
                </TabContent>
              </Tabs>
            </div>
            <div
              className={`relative duration-300 w-full md:w-fit md:min-w-[300px] pt-kit-32 pb-kit-64 px-kit-48 rounded-t-kit-2xl border-[1px] border-[--inyc-input-border-color] bg-[var(--inyc-primary)] shadow-md`}
            >
              <div className="flex flex-wrap justify-center items-stretch  gap-kit-8 mb-kit-16">
                {(availableApps?.native || navigator.share) && (
                  <ActionTile onClick={shareLink} icon={<IoIosLink />}>
                    share link
                  </ActionTile>
                )}
                {availableApps?.instagram && (
                  <ActionTile onClick={shareInstagramStory} icon={<FaInstagram />}>
                    post story
                  </ActionTile>
                )}
                {availableApps?.native && (
                  <ActionTile onClick={shareImage} icon={<TbPhotoShare />}>
                    share image
                  </ActionTile>
                )}
                <ActionTile onClick={copyLink} icon={<IoCopyOutline />}>
                    copy link
                  </ActionTile>
                <ActionTile onClick={() => setTab("qr")} icon={<MdQrCode2 />}>
                    QR code
                  </ActionTile>
                {availableApps?.native && (
                  <ActionTile onClick={shareQrCode} icon={<TbShare3 />}>
                    share QR code
                  </ActionTile>
                )}
              </div>
              <IconButton
                size="md"
                label="close"
                icon={<IoMdClose />}
                className="absolute top-[0.5rem] right-[1rem]"
                onClick={() => proceed(false)}
              />
            </div>
          </div>
        );
      },
      {
        raw: true,
        fullScreen: true,
      }
    );
    } catch (error) {
      console.error("Error opening account share options:", error);
      enqueueSnackbar("unable to open share options.", {
        variant: "error",
      });
    }
  };

  const toggleFollow = async () => {
    toggleFollowUser(account?._id);

    axios
      .get(`/user/follow/${account?._id}`)
      .then((res) => {
        if (res.data.error) {
          enqueueSnackbar(res.data.error, { variant: "error" });

          toggleFollowUser(account?._id);
        }
      })
      .catch((err) => {
        console.log(err);

        toggleFollowUser(account?._id);
      });
  };

  return (
    <div>
      {account ? (
        <div className="px-kit-24"
        style={{
          "--inyc-details-btn-bg": "var(--inyc-primary-button)",
          "--inyc-details-border-color": "var(--inyc-input-border-color)",
          "--inyc-details-light-color": "var(--inyc-primary-text-color)",
        }}
        >
          <div className="relative w-fit mx-auto mb-kit-32">
            <ProfilePicture
              className={"text-kit-2xl"}
              profilePicture={account?.profilePicture}
              username={(() => {
                const fn = account?.firstName?.charAt(0) || '';
                const ln = account?.lastName?.charAt(0) || '';
                if (fn && ln) return (fn + ln).toLowerCase();
                if (account?.businessName?.trim()) {
                  const w = account.businessName.trim().split(/\s+/);
                  if (w.length >= 2) return (w[0][0] + w[w.length - 1][0]).toLowerCase();
                  return account.businessName.slice(0, 2).toLowerCase();
                }
                return (fn + ln).toLowerCase();
              })()}
              style={{
                width: "150px",
                height: "150px",
                border: "1px solid var(--inyc-input-border-color)",
              }}
            />
            {/* Points badge - bottom right of profile picture */}
            {account?.points !== undefined && account?.points > 0 && (
              <Badge className="absolute bottom-[1rem] right-[-4.5rem] gap-kit-4">
                <span>
                  {account.points} {account.points === 1 ? 'pt' : 'pts'}
                </span>
                <div className="relative group inline-block">
                  <TbInfoSquareRoundedFilled 
                    size={12} 
                    className="text-[var(--inyc-primary-text-color-light)] opacity-50 cursor-help"
                  />
                  <div className="absolute left-auto right-0 bottom-[calc(100%+5px)] px-kit-12 py-kit-8 bg-[var(--inyc-primary)] border-[1px] border-[var(--inyc-input-border-color)] rounded-kit-xs shadow-lg opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200 z-[100] w-[250px]">
                    <div className="text-kit-xs font-['Primary-Regular'] text-[var(--inyc-primary-text-color)]">
                      points are earned by referring friends (see referral code below) or posting events + venues. collect enough and you'll start unlocking things worth talking about.
                    </div>
                  </div>
                </div>
              </Badge>
            )}
          </div>
          <div className="mb-kit-8">
            <div className="flex justify-end">
              <IconButton
                size="md"
                label="close"
                icon={<IoMdClose size={24} />}
                onClick={() => {
                  setSelectedAccount(null);
                  Close && Close();
                }}
              />
            </div>
            <div className="inyc-display min-w-0 w-full">
              {account?.businessName ||
                account?.firstName + " " + account?.lastName}
            </div>
          </div>
          {account?.businessName && (() => {
            const personal = `${account?.firstName || ""} ${account?.lastName || ""}`.trim();
            const isGuestPlaceholder = personal === "Guest User";
            const dup =
              personal &&
              account.businessName &&
              personal.toLowerCase() === account.businessName.trim().toLowerCase();
            if (!personal || isGuestPlaceholder || dup) return null;
            return (
              <div className="inyc-help mb-kit-16">
                {personal}
              </div>
            );
          })()}
          {account?.bio && (
            <div className="mb-kit-16">
              <div className="text-kit-md font-bold" style={{ whiteSpace: "pre-wrap" }}>
                {showFullBio ? account.bio : account.bio.slice(0, characterLimit)}
                {!showFullBio && account.bio.length > characterLimit ? "..." : ""}
              </div>
              {account.bio.length > characterLimit && (
                <div className="flex justify-end mt-kit-6">
                  <KitButton
                    variant="tertiary"
                    size="sm"
                    aria-expanded={showFullBio}
                    onClick={() => setShowFullBio((isExpanded) => !isExpanded)}
                  >
                    {showFullBio ? "show less" : "show more"}
                  </KitButton>
                </div>
              )}
            </div>
          )}
          <div className="event-details-action-socials-row gap-kit-16 w-full mb-kit-16">
            <IconButton
              variant="secondary"
              shape="box"
              size="md"
              className="event-details-action-btn"
              label={user?.following?.includes(account?._id) ? "watching this artist!" : "watch"}
              icon={user?.following?.includes(account?._id) ? <FaEye size={20} /> : <FaRegEye size={20} />}
              onClick={toggleFollow}
            />
            <IconButton
              variant="secondary"
              shape="box"
              size="md"
              className="event-details-action-btn"
              label="share"
              icon={<TbShare3 size={20} />}
              onClick={shareAccount}
            />
            {account.socials?.length > 0 && (
              <div className="event-details-socials-cluster">
                <SocialsView socials={account.socials} variant="icons" />
              </div>
            )}
          </div>
          {hasMediaEmbeds(account.socials) && (
            <div className="mb-kit-16">
              <SocialsView socials={account.socials} variant="media" />
            </div>
          )}
          {/* //TODO make it disabled for non posting accounts */}
          {true && (
            <div className="mb-[5rem]">
              <>
                <div className="relative mx-kit-16 flex items-center gap-kit-8">
                  <Input
                    placeHolder="search for this artist's events"
                    Ref={searchInputRef}
                    onChange={(e) => setQueryFilter(e.target.value)}
                    value={queryFilter}
                    icon={<FiSearch size={16} />}
                    onKeyDown={handleSearchKeyDown}
                    className={
                      "search-filters-input !mt-kit-8 !mb-kit-2 md:!mb-kit-32 !mx-0"
                    }
                  />
                  {queryFilter && queryFilter.trim().length > 0 ? (
                    <IconButton
                      size="xs"
                      label="clear search"
                      icon={<IoMdClose />}
                      className="absolute top-1/2 md:top-[1.45rem] right-[0.5rem] -translate-y-1/2 z-10"
                      onClick={() => setQueryFilter("")}
                    />
                  ) : (
                    <Chip
                      aria-label="filters"
                      className="shrink-0"
                      selected={Boolean(filtersOpen || tagFilter.length > 0 || dateFilter)}
                      icon={<IoFilter size={16} />}
                      onClick={() => setFiltersOpen(!filtersOpen)}
                    />
                  )}
                </div>
                <div
                  className={`grid overflow-hidden duration-200 ${
                    filtersOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="min-h-0">
                    <BottomSheetDateFilter
                      filterDate={dateFilter}
                      setFilterDate={setDateFilter}
                      tagFilter={tagFilter}
                      setTagFilter={setTagFilter}
                      className={"!relative md:!min-w-full md:!w-full"}
                      hideFilterIcon={true}
                      alwaysShowTags={true}
                    />
                  </div>
                </div>
                {accountDetailsSearchLoading && (
                  <div className="fixed left-[50%] translate-x-[-50%] p-[5rem] ">
                    <Loader />
                  </div>
                )}
                {filteredEvents?.length > 0 ? (
                  <>
                    <InfiniteScroller
                      className={
                        accountDetailsSearchLoading ? "opacity-20" : ""
                      }
                      items={[
                        ...(filtersActive
                          ? filteredEvents?.map((e) => {
                              e._filterEvent = true;
                              return e;
                            }) || []
                          : []),
                        ...(filtersActive
                          ? filteredEvents?.filter(
                              (l) =>
                                !filteredEvents?.some((fl) => fl._id === l._id)
                            )
                          : filteredEvents || []),
                      ]}
                      renderer={(item, index) => {
                        return (
                          <EventCard
                            key={index}
                            disabled={filtersActive && !item._filterEvent}
                            data={{ ...item, timezone: item.timezone || timezone }}
                            className={`map-event-container p-0!`}
                            fullWidth
                            onClick={() => {
                              selectEvent(item, item._matchingEvent);
                              setSelectedAccount(null);
                            }}
                          />
                        );
                      }}
                    />
                  </>
                ) : (
                  <div className="map-events-none">no upcoming events</div>
                )}
              </>
            </div>
          )}
          {/* {JSON.stringify(account, null, 2)} */}
        </div>
      ) : (
        <div className="py-kit-32">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default AccountDetails;
